import dayjs from 'dayjs';
import DOMPurify from 'dompurify';

import { COMMON_DATE_FORMAT } from 'constants/date';

import {
  CLOSED_CONTACT_TASK_STATUSES,
  ContactLifecycleStageColor,
  ContactLifecycleStageLabel,
} from './crm.constants';

export const getContactFullName = ({ firstName, lastName }) =>
  [firstName, lastName].filter(Boolean).join(' ');

export const getPrimaryEmail = (emails = []) => {
  const list = emails || [];

  return list.find(({ isPrimary }) => isPrimary)?.emailAddress || list[0]?.emailAddress || '';
};

export const getPrimaryPhone = (phones = []) => {
  const list = phones || [];

  return list.find(({ isPrimary }) => isPrimary)?.phoneNumber || list[0]?.phoneNumber || '';
};

/**
 * Builds the request for a contact update — the target contact plus only the fields being changed.
 *
 * PUT /api/contacts/{id} merges: Lender.Optimus.CRM ContactsRepository.Update guards every field
 * with `is not null` / `.HasValue`, so an omitted field means "leave unchanged". Echoing untouched
 * fields therefore buys nothing and costs correctness — a save still in flight would carry the
 * values it read at build time and overwrite whatever a concurrent edit changed since (DEV-6193).
 * @param {Object} contact
 * @param {Object} patch
 * @returns {Object}
 */
export const buildContactUpdateDto = (contact, patch) => ({
  contactId: contact.id,
  ...patch,
});

/**
 * The tasks still owed on a contact — what the Tasks tab badge counts and the tasks card lists.
 * @param {Array} tasks
 * @returns {Array}
 */
export const getOpenContactTasks = (tasks = []) =>
  tasks.filter(({ status }) => !CLOSED_CONTACT_TASK_STATUSES.includes(status));

/**
 * Whether a picker's text is a replacement being typed rather than the committed selection.
 *
 * The pickers commit on change, and MUI's clear button discards the committed selection along with
 * the draft text — so leaving it up while someone is typing a replacement turns "abandon my search"
 * into an immediate unassign. Every clearable picker suppresses its clear affordance while this is
 * true, so the rule lives here rather than in each surface.
 * @param {string} searchText
 * @param {?string} selectedOptionName
 * @returns {boolean}
 */
export const isReplacingSelection = (searchText, selectedOptionName) =>
  !!searchText && searchText !== (selectedOptionName ?? '');

/**
 * Toast copy for a saved owner change — assigning and clearing read differently.
 * @param {?string} assignedUserId
 * @returns {string}
 */
export const getOwnerSavedMessage = assignedUserId =>
  assignedUserId ? 'Owner updated' : 'Owner removed';

/**
 * Owner picker options — real people only.
 *
 * Deliberately carries no "None" entry: a sentinel option makes the unassigned state render as a
 * person literally named None instead of as empty. Unassigning is the clear affordance's job, so
 * the empty state stays a null value and the surfaces show placeholder copy for it.
 * @param {Array} users
 * @returns {Array}
 */
export const buildOwnerOptions = (users = []) =>
  (users || []).map(({ id, name }) => ({ value: id, name }));

export const getLifecycleStageLabel = stage => ContactLifecycleStageLabel[stage] || stage || '';

export const getLifecycleStageColor = stage => ContactLifecycleStageColor[stage] || 'GRAY';

export const formatDate = date => (date ? dayjs(date).format(COMMON_DATE_FORMAT) : '');

export const formatDateTime = date => (date ? dayjs(date).format('MM/DD/YYYY hh:mm A') : '');

export const hasAuditTrail = note => !!note.modifiedDate && note.modifiedDate !== note.createdDate;

// Notes are rich text (HTML). Sanitize on render — mirrors the loan underwriting
// Notes pattern (underwritingCDS/Notes/Note.js). The store holds raw HTML, so this
// render-time gate is the XSS boundary.
export const sanitizeNoteHtml = html => ({
  __html: DOMPurify.sanitize(html || '', { WHOLE_DOCUMENT: true }),
});

// Plain-text preview of a rich-text note, for single-line surfaces (the activity feed)
// that can't render HTML — strips tags and decodes the common entities CKEditor emits.
export const getNotePlainText = html =>
  (html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();

// A rich-text value is "empty" when it carries no visible text or embedded media —
// an empty CKEditor still emits markup like <p>&nbsp;</p>. Used to gate Save.
export const isRichTextEmpty = html => {
  if (!html) {
    return true;
  }

  const hasEmbeddedMedia = /<(img|table|iframe|video|audio|hr)\b/i.test(html);
  const visibleText = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s/g, '');

  return !hasEmbeddedMedia && visibleText.length === 0;
};

export const sortNotesByPinnedAndDate = (notes = []) => {
  const pinnedNotes = notes
    .filter(({ isPinned }) => isPinned)
    .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

  const unpinnedNotes = notes
    .filter(({ isPinned }) => !isPinned)
    .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

  return [...pinnedNotes, ...unpinnedNotes];
};
